import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const OpeningBalances = () => {
  const [balances, setBalances] = useState([
    {
      id: 1,
      accountName: 'Cash',
      accountCode: '1000',
      accountType: 'Asset',
      debitBalance: 25000,
      creditBalance: 0,
      currency: 'USD',
      description: 'Cash on hand',
      isLocked: false
    },
    {
      id: 2,
      accountName: 'Accounts Receivable',
      accountCode: '1200',
      accountType: 'Asset',
      debitBalance: 15000,
      creditBalance: 0,
      currency: 'USD',
      description: 'Outstanding customer invoices',
      isLocked: false
    },
    {
      id: 3,
      accountName: 'Accounts Payable',
      accountCode: '2000',
      accountType: 'Liability',
      debitBalance: 0,
      creditBalance: 8500,
      currency: 'USD',
      description: 'Outstanding vendor bills',
      isLocked: false
    },
    {
      id: 4,
      accountName: 'Retained Earnings',
      accountCode: '3000',
      accountType: 'Equity',
      debitBalance: 0,
      creditBalance: 31500,
      currency: 'USD',
      description: 'Previous year retained earnings',
      isLocked: true
    }
  ]);

  const [settings, setSettings] = useState({
    openingDate: '2025-01-01',
    baseCurrency: 'USD',
    enableMultiCurrency: true,
    autoCalculateEquity: true,
    requireApproval: true,
    lockAfterApproval: true
  });

  const [showAddModal, setShowAddModal] = useState(false);
  const [editingBalance, setEditingBalance] = useState(null);
  const [newBalance, setNewBalance] = useState({
    accountName: '',
    accountCode: '',
    accountType: 'Asset',
    debitBalance: 0,
    creditBalance: 0,
    currency: 'USD',
    description: ''
  });

  const [isApproved, setIsApproved] = useState(false);

  const accountTypes = ['Asset', 'Liability', 'Equity', 'Revenue', 'Expense'];
  const currencies = ['USD', 'EUR', 'GBP', 'JPY', 'CAD'];

  const calculateTotals = () => {
    const totalDebits = balances?.reduce((sum, balance) => sum + (balance?.debitBalance || 0), 0);
    const totalCredits = balances?.reduce((sum, balance) => sum + (balance?.creditBalance || 0), 0);
    const difference = totalDebits - totalCredits;
    return { totalDebits, totalCredits, difference };
  };

  const handleAddBalance = () => {
    const balance = {
      ...newBalance,
      id: Math.max(...balances?.map(b => b?.id), 0) + 1,
      isLocked: false
    };
    setBalances(prev => [...prev, balance]);
    resetForm();
  };

  const handleUpdateBalance = () => {
    setBalances(prev => 
      prev?.map(balance => 
        balance?.id === editingBalance?.id ? { ...editingBalance, ...newBalance } : balance
      )
    );
    resetForm();
  };

  const handleDeleteBalance = (balanceId) => {
    setBalances(prev => prev?.filter(balance => balance?.id !== balanceId));
  };

  const resetForm = () => {
    setNewBalance({
      accountName: '',
      accountCode: '',
      accountType: 'Asset',
      debitBalance: 0,
      creditBalance: 0,
      currency: 'USD',
      description: ''
    });
    setEditingBalance(null);
    setShowAddModal(false);
  };

  const handleEditBalance = (balance) => {
    setEditingBalance(balance);
    setNewBalance({
      accountName: balance?.accountName,
      accountCode: balance?.accountCode,
      accountType: balance?.accountType,
      debitBalance: balance?.debitBalance,
      creditBalance: balance?.creditBalance,
      currency: balance?.currency,
      description: balance?.description
    });
    setShowAddModal(true);
  };

  const updateBalance = (balanceId, field, value) => {
    if (isApproved && settings?.lockAfterApproval) return;
    
    setBalances(prev => 
      prev?.map(balance => 
        balance?.id === balanceId 
          ? { ...balance, [field]: parseFloat(value) || 0 }
          : balance
      )
    );
  };

  const handleApproval = () => {
    setIsApproved(true);
    if (settings?.lockAfterApproval) {
      setBalances(prev => 
        prev?.map(balance => ({ ...balance, isLocked: true }))
      );
    }
  };

  const importFromFile = () => {
    console.log('Importing opening balances from file...');
    // TODO: Implement file import functionality
  };

  const exportBalances = () => {
    console.log('Exporting opening balances...');
    // TODO: Implement export functionality
  };

  const { totalDebits, totalCredits, difference } = calculateTotals();

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-foreground">Opening Balances</h2>
          <p className="text-muted-foreground">Set up your chart of accounts with opening balances for the fiscal year</p>
        </div>
        <div className="flex space-x-3">
          <Button variant="outline" onClick={importFromFile} iconName="Upload">
            Import
          </Button>
          <Button variant="outline" onClick={exportBalances} iconName="Download">
            Export
          </Button>
          <Button onClick={() => setShowAddModal(true)} iconName="Plus">
            Add Account
          </Button>
        </div>
      </div>
      {/* Settings */}
      <div className="bg-surface border border-border rounded-lg p-6">
        <h3 className="text-lg font-medium text-foreground mb-6">Opening Balance Settings</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <Input
            label="Opening Date"
            type="date"
            value={settings?.openingDate}
            onChange={(e) => setSettings(prev => ({ ...prev, openingDate: e?.target?.value }))}
            description="Start date for opening balances"
          />

          <div>
            <label className="text-sm font-medium text-foreground mb-2 block">
              Base Currency
            </label>
            <select
              value={settings?.baseCurrency}
              onChange={(e) => setSettings(prev => ({ ...prev, baseCurrency: e?.target?.value }))}
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            >
              {currencies?.map(currency => (
                <option key={currency} value={currency}>{currency}</option>
              ))}
            </select>
          </div>

          <div className="space-y-4">
            <label className="flex items-center justify-between cursor-pointer">
              <div>
                <span className="text-sm font-medium text-foreground">Enable Multi-Currency</span>
                <p className="text-xs text-muted-foreground">Support multiple currencies</p>
              </div>
              <input
                type="checkbox"
                checked={settings?.enableMultiCurrency}
                onChange={(e) => setSettings(prev => ({ ...prev, enableMultiCurrency: e?.target?.checked }))}
                className="h-4 w-4 rounded border border-input bg-background text-primary focus:ring-2 focus:ring-ring focus:ring-offset-2"
              />
            </label>

            <label className="flex items-center justify-between cursor-pointer">
              <div>
                <span className="text-sm font-medium text-foreground">Auto-Calculate Equity</span>
                <p className="text-xs text-muted-foreground">Automatically balance equity accounts</p>
              </div>
              <input
                type="checkbox"
                checked={settings?.autoCalculateEquity}
                onChange={(e) => setSettings(prev => ({ ...prev, autoCalculateEquity: e?.target?.checked }))}
                className="h-4 w-4 rounded border border-input bg-background text-primary focus:ring-2 focus:ring-ring focus:ring-offset-2"
              />
            </label>

            <label className="flex items-center justify-between cursor-pointer">
              <div>
                <span className="text-sm font-medium text-foreground">Lock After Approval</span>
                <p className="text-xs text-muted-foreground">Prevent changes after approval</p>
              </div>
              <input
                type="checkbox"
                checked={settings?.lockAfterApproval}
                onChange={(e) => setSettings(prev => ({ ...prev, lockAfterApproval: e?.target?.checked }))}
                className="h-4 w-4 rounded border border-input bg-background text-primary focus:ring-2 focus:ring-ring focus:ring-offset-2"
              />
            </label>
          </div>
        </div>
      </div>
      {/* Balance Summary */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-surface border border-border rounded-lg p-6 text-center">
          <Icon name="TrendingUp" size={24} className="mx-auto mb-2 text-green-500" />
          <h3 className="text-2xl font-semibold text-foreground">${totalDebits?.toLocaleString()}</h3>
          <p className="text-sm text-muted-foreground">Total Debits</p>
        </div>
        
        <div className="bg-surface border border-border rounded-lg p-6 text-center">
          <Icon name="TrendingDown" size={24} className="mx-auto mb-2 text-red-500" />
          <h3 className="text-2xl font-semibold text-foreground">${totalCredits?.toLocaleString()}</h3>
          <p className="text-sm text-muted-foreground">Total Credits</p>
        </div>
        
        <div className="bg-surface border border-border rounded-lg p-6 text-center">
          <Icon name="Scale" size={24} className={`mx-auto mb-2 ${difference === 0 ? 'text-green-500' : 'text-yellow-500'}`} />
          <h3 className={`text-2xl font-semibold ${difference === 0 ? 'text-green-600' : 'text-yellow-600'}`}>
            ${Math?.abs(difference)?.toLocaleString()}
          </h3>
          <p className="text-sm text-muted-foreground">
            {difference === 0 ? 'Balanced' : 'Out of Balance'}
          </p>
        </div>

        <div className="bg-surface border border-border rounded-lg p-6 text-center">
          <Icon name="CheckCircle" size={24} className={`mx-auto mb-2 ${isApproved ? 'text-green-500' : 'text-muted-foreground'}`} />
          <h3 className="text-lg font-semibold text-foreground">{isApproved ? 'Approved' : 'Draft'}</h3>
          <p className="text-sm text-muted-foreground">
            {isApproved ? 'Ready for use' : 'Pending approval'}
          </p>
        </div>
      </div>
      {/* Opening Balances Table */}
      <div className="bg-surface border border-border rounded-lg p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-medium text-foreground">Account Balances</h3>
          {difference === 0 && !isApproved && (
            <Button onClick={handleApproval} iconName="Check">
              Approve Balances
            </Button>
          )}
        </div>
        
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-muted/30">
              <tr>
                <th className="text-left p-3 text-sm font-medium text-foreground">Account</th>
                <th className="text-left p-3 text-sm font-medium text-foreground">Code</th>
                <th className="text-left p-3 text-sm font-medium text-foreground">Type</th>
                <th className="text-right p-3 text-sm font-medium text-foreground">Debit</th>
                <th className="text-right p-3 text-sm font-medium text-foreground">Credit</th>
                <th className="text-left p-3 text-sm font-medium text-foreground">Currency</th>
                <th className="text-left p-3 text-sm font-medium text-foreground">Description</th>
                <th className="text-left p-3 text-sm font-medium text-foreground">Actions</th>
              </tr>
            </thead>
            <tbody>
              {balances?.map((balance) => (
                <tr key={balance?.id} className="border-t border-border hover:bg-muted/20">
                  <td className="p-3">
                    <div className="flex items-center space-x-2">
                      <span className="font-medium text-foreground">{balance?.accountName}</span>
                      {balance?.isLocked && (
                        <Icon name="Lock" size={14} className="text-muted-foreground" />
                      )}
                    </div>
                  </td>
                  <td className="p-3">
                    <span className="font-mono text-sm">{balance?.accountCode}</span>
                  </td>
                  <td className="p-3">
                    <span className={`px-2 py-1 text-xs font-medium rounded ${
                      balance?.accountType === 'Asset' ? 'bg-blue-100 text-blue-700' :
                      balance?.accountType === 'Liability' ? 'bg-red-100 text-red-700' :
                      balance?.accountType === 'Equity' ? 'bg-green-100 text-green-700' :
                      balance?.accountType === 'Revenue'? 'bg-purple-100 text-purple-700' : 'bg-orange-100 text-orange-700'
                    }`}>
                      {balance?.accountType}
                    </span>
                  </td>
                  <td className="p-3 text-right">
                    {balance?.isLocked ? (
                      <span className="font-medium">${balance?.debitBalance?.toLocaleString()}</span>
                    ) : (
                      <Input
                        type="number"
                        value={balance?.debitBalance}
                        onChange={(e) => updateBalance(balance?.id, 'debitBalance', e?.target?.value)}
                        step="0.01"
                        min="0"
                        className="w-24 text-right text-sm"
                        disabled={balance?.isLocked}
                      />
                    )}
                  </td>
                  <td className="p-3 text-right">
                    {balance?.isLocked ? (
                      <span className="font-medium">${balance?.creditBalance?.toLocaleString()}</span>
                    ) : (
                      <Input
                        type="number"
                        value={balance?.creditBalance}
                        onChange={(e) => updateBalance(balance?.id, 'creditBalance', e?.target?.value)}
                        step="0.01"
                        min="0"
                        className="w-24 text-right text-sm"
                        disabled={balance?.isLocked}
                      />
                    )}
                  </td>
                  <td className="p-3">
                    <span className="text-sm">{balance?.currency}</span>
                  </td>
                  <td className="p-3">
                    <span className="text-sm text-muted-foreground">{balance?.description}</span>
                  </td>
                  <td className="p-3">
                    <div className="flex items-center space-x-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleEditBalance(balance)}
                        disabled={balance?.isLocked}
                        iconName="Edit"
                      >
                        Edit
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDeleteBalance(balance?.id)}
                        disabled={balance?.isLocked}
                        iconName="Trash2"
                      >
                        Delete
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot className="bg-muted/20 border-t-2 border-border">
              <tr>
                <td colSpan="3" className="p-3 font-medium text-foreground">Totals:</td>
                <td className="p-3 text-right font-semibold text-foreground">
                  ${totalDebits?.toLocaleString()}
                </td>
                <td className="p-3 text-right font-semibold text-foreground">
                  ${totalCredits?.toLocaleString()}
                </td>
                <td colSpan="3" className="p-3">
                  <span className={`text-sm font-medium ${difference === 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {difference === 0 ? '✓ Balanced' : `⚠ Out of balance by $${Math?.abs(difference)?.toLocaleString()}`}
                  </span>
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
      {/* Add/Edit Balance Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-surface border border-border rounded-lg w-full max-w-2xl">
            <div className="flex items-center justify-between p-6 border-b border-border">
              <h3 className="text-lg font-semibold text-foreground">
                {editingBalance ? 'Edit Account Balance' : 'Add Account Balance'}
              </h3>
              <Button variant="ghost" size="icon" onClick={resetForm}>
                <Icon name="X" size={16} />
              </Button>
            </div>

            <div className="p-6 space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  label="Account Name"
                  value={newBalance?.accountName}
                  onChange={(e) => setNewBalance(prev => ({ ...prev, accountName: e?.target?.value }))}
                  placeholder="Enter account name"
                  required
                />

                <Input
                  label="Account Code"
                  value={newBalance?.accountCode}
                  onChange={(e) => setNewBalance(prev => ({ ...prev, accountCode: e?.target?.value }))}
                  placeholder="Enter account code"
                  required
                />

                <div>
                  <label className="text-sm font-medium text-foreground mb-2 block">
                    Account Type
                  </label>
                  <select
                    value={newBalance?.accountType}
                    onChange={(e) => setNewBalance(prev => ({ ...prev, accountType: e?.target?.value }))}
                    className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                  >
                    {accountTypes?.map(type => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="text-sm font-medium text-foreground mb-2 block">
                    Currency
                  </label>
                  <select
                    value={newBalance?.currency}
                    onChange={(e) => setNewBalance(prev => ({ ...prev, currency: e?.target?.value }))}
                    className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                  >
                    {currencies?.map(currency => (
                      <option key={currency} value={currency}>{currency}</option>
                    ))}
                  </select>
                </div>

                <Input
                  label="Debit Balance"
                  type="number"
                  value={newBalance?.debitBalance}
                  onChange={(e) => setNewBalance(prev => ({ ...prev, debitBalance: parseFloat(e?.target?.value) || 0 }))}
                  step="0.01"
                  min="0"
                />

                <Input
                  label="Credit Balance"
                  type="number"
                  value={newBalance?.creditBalance}
                  onChange={(e) => setNewBalance(prev => ({ ...prev, creditBalance: parseFloat(e?.target?.value) || 0 }))}
                  step="0.01"
                  min="0"
                />
              </div>

              <div>
                <label className="text-sm font-medium text-foreground mb-2 block">
                  Description
                </label>
                <textarea
                  value={newBalance?.description}
                  onChange={(e) => setNewBalance(prev => ({ ...prev, description: e?.target?.value }))}
                  placeholder="Enter account description"
                  rows={3}
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                />
              </div>
            </div>

            <div className="flex justify-end space-x-4 p-6 border-t border-border">
              <Button variant="outline" onClick={resetForm}>
                Cancel
              </Button>
              <Button 
                onClick={editingBalance ? handleUpdateBalance : handleAddBalance}
                iconName="Save"
              >
                {editingBalance ? 'Update Balance' : 'Add Balance'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OpeningBalances;